import React, { FC } from 'react';
import { useNavigate } from 'react-router-dom';

import { useManageItemStyle } from './ManageItem.style';
import { PATHS } from 'src/routes/paths';

import { Vault } from '@/shared/api/vault';
import { formatSmart } from '@/shared/utils/helpers';
import { AssetWithIcon, TooltipWrapperDisabled } from '@/shared/ui';
import { cn } from '@/shared/lib/tailwind-merge';

const VAULT_STATUS = {
    NEW: 'new',
    ACTIVE: 'active',
    SUSPENDED: 'suspended',
};

type ManageItemProps = {
    vault: Vault;
};

export const ManageItem: FC<ManageItemProps> = ({ vault }) => {
    const classes = useManageItemStyle();
    const navigate = useNavigate();

    const handleOnClick = () => {
        navigate(PATHS.VAULTS_MANAGE + '/' + vault.address);
    };

    const getStatusBadge = (status: string) => {
        const statusMap = {
            [VAULT_STATUS.NEW]: {
                className: classes.badgeNew,
                content: VAULT_STATUS.NEW,
            },
            [VAULT_STATUS.ACTIVE]: {
                className: classes.badgeActive,
                content: VAULT_STATUS.ACTIVE,
            },
            [VAULT_STATUS.SUSPENDED]: {
                className: classes.badgeSuspended,
                content: VAULT_STATUS.SUSPENDED,
            },
        };

        const { className = '', content = 'Unknown Status' } = statusMap[status] || {};

        return <div className={cn(classes.badge, className)}>{content}</div>;
    };

    const netApy = Number(vault.netApy || 0)
        ?.toFixed(2)
        .replace(/\.?0+$/, '');

    const component = (
        <button className={classes.card} onClick={handleOnClick}>
            <div className={classes.item}>
                <div className={classes.cell}>
                    <div className={classes.label}>{getStatusBadge('new')}</div>
                    <div className={classes.row}>{vault.title}</div>
                </div>
            </div>

            <div className={classes.item}>
                <div className={classes.cell}>
                    <div className={classes.label}>Token</div>
                    <div className={classes.row}>
                        <AssetWithIcon assetName={vault.leverage.symbol || 'DAI'} />
                    </div>
                </div>
            </div>

            <div className={classes.item}>
                <div className={classes.cell}>
                    <div className={classes.label}>Total Supply</div>
                    <div className={classes.row}>{formatSmart(vault.totalDeposited!)}</div>
                </div>
            </div>

            <div className={classes.item}>
                <div className={classes.cell}>
                    <div className={classes.label}>Net APY</div>
                    <div className={classes.row}>
                        {Number.isNaN(Number(netApy)) ? 'N/A' : `${netApy} %`}
                    </div>
                </div>
            </div>

            <div className={classes.item}>
                <div className={classes.cell}>
                    <div className={classes.label}>Performance Fee</div>
                    <div className={classes.row}>{vault.performanceFee} %</div>
                </div>
            </div>
        </button>
    );

    return (
        <>
            {vault.errorContract ? (
                <TooltipWrapperDisabled
                    text={'Vault Contract is not available'}
                    component={component}
                    sideOffset={-20}
                    className='w-[100%]'
                />
            ) : (
                component
            )}
        </>
    );
};
